import React, { useState, useEffect } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Plus,
  Pencil,
  Trash2,
  User,
  Briefcase,
  Mail,
  Linkedin,
  Twitter,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';

interface Expert {
  id: string;
  site_id: string;
  name: string;
  email?: string;
  role?: string;
  company?: string;
  bio?: string;
  expertise?: string[];
  photo_url?: string;
  linkedin_url?: string;
  twitter_url?: string;
  website_url?: string;
  created_at: string;
  updated_at: string;
  creator_name?: string;
}

export default function ExpertsManagement() {
  const { selectedSite } = useSite();
  const [experts, setExperts] = useState<Expert[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingExpert, setEditingExpert] = useState<Expert | null>(null);
  const [deletingExpert, setDeletingExpert] = useState<Expert | null>(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    company: '',
    bio: '',
    expertise: '',
    photo_url: '',
    linkedin_url: '',
    twitter_url: '',
    website_url: ''
  });

  useEffect(() => {
    if (selectedSite?.id) {
      fetchExperts();
    }
  }, [selectedSite]);

  const fetchExperts = async () => {
    if (!selectedSite?.id) return;

    try {
      setLoading(true);
      const response = await authFetch(`/api/experts?siteId=${selectedSite.id}`);

      if (!response.ok) {
        throw new Error('Failed to fetch experts');
      }

      const data = await response.json();
      setExperts(data.experts || []);
    } catch (error) {
      console.error('Error fetching experts:', error);
      toast.error('Failed to load experts');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (expert?: Expert) => {
    if (expert) {
      setEditingExpert(expert);
      setFormData({
        name: expert.name || '',
        email: expert.email || '',
        role: expert.role || '',
        company: expert.company || '',
        bio: expert.bio || '',
        expertise: expert.expertise?.join(', ') || '',
        photo_url: expert.photo_url || '',
        linkedin_url: expert.linkedin_url || '',
        twitter_url: expert.twitter_url || '',
        website_url: expert.website_url || ''
      });
    } else {
      setEditingExpert(null);
      setFormData({
        name: '',
        email: '',
        role: '',
        company: '',
        bio: '',
        expertise: '',
        photo_url: '',
        linkedin_url: '',
        twitter_url: '',
        website_url: ''
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingExpert(null);
    setFormData({
      name: '',
      email: '',
      role: '',
      company: '',
      bio: '',
      expertise: '',
      photo_url: '',
      linkedin_url: '',
      twitter_url: '',
      website_url: ''
    });
  };

  const handleSaveExpert = async () => {
    if (!selectedSite?.id) return;
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      setSaving(true);

      // Convert expertise string to array
      const expertiseArray = formData.expertise
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0);

      const payload = {
        siteId: selectedSite.id,
        name: formData.name,
        email: formData.email || null,
        role: formData.role || null,
        company: formData.company || null,
        bio: formData.bio || null,
        expertise: expertiseArray.length > 0 ? expertiseArray : null,
        photo_url: formData.photo_url || null,
        linkedin_url: formData.linkedin_url || null,
        twitter_url: formData.twitter_url || null,
        website_url: formData.website_url || null
      };

      let response;
      if (editingExpert) {
        // Update existing expert
        response = await authFetch(`/api/experts/${editingExpert.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        // Create new expert
        response = await authFetch('/api/experts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save expert');
      }

      toast.success(editingExpert ? 'Expert updated successfully' : 'Expert created successfully');
      handleCloseDialog();
      fetchExperts();
    } catch (error: any) {
      console.error('Error saving expert:', error);
      toast.error(error.message || 'Failed to save expert');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExpert = async () => {
    if (!deletingExpert) return;

    try {
      setSaving(true);
      const response = await authFetch(`/api/experts/${deletingExpert.id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete expert');
      }

      toast.success('Expert deleted successfully');
      setIsDeleteDialogOpen(false);
      setDeletingExpert(null);
      fetchExperts();
    } catch (error) {
      console.error('Error deleting expert:', error);
      toast.error('Failed to delete expert');
    } finally {
      setSaving(false);
    }
  };

  if (!selectedSite) {
    return (
      <div className="p-8 text-center">
        <p className="text-muted-foreground">Please select a site to manage experts</p>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Experts Management</h1>
          <p className="text-muted-foreground mt-1">
            Manage expert profiles for content interviews
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="gap-2">
          <Plus className="w-4 h-4" />
          Add Expert
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <p className="text-muted-foreground">Loading experts...</p>
        </div>
      ) : experts.length === 0 ? (
        <div className="border-2 border-dashed rounded-lg p-12 text-center">
          <User className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No experts yet</h3>
          <p className="text-muted-foreground mb-4">
            Get started by creating your first expert profile
          </p>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Add Expert
          </Button>
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Expertise</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {experts.map((expert) => (
                <TableRow key={expert.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {expert.photo_url ? (
                        <img
                          src={expert.photo_url}
                          alt={expert.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-purple-600" />
                        </div>
                      )}
                      <div>
                        <div className="font-medium">{expert.name}</div>
                        {expert.email && (
                          <div className="text-sm text-muted-foreground flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {expert.email}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {expert.role && (
                      <div className="flex items-center gap-1 text-sm">
                        <Briefcase className="w-3 h-3 text-muted-foreground" />
                        {expert.role}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {expert.company || '-'}
                  </TableCell>
                  <TableCell>
                    {expert.expertise && expert.expertise.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {expert.expertise.slice(0, 3).map((area, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {area}
                          </Badge>
                        ))}
                        {expert.expertise.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{expert.expertise.length - 3}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {expert.linkedin_url && (
                        <a
                          href={expert.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <Linkedin className="w-4 h-4" />
                        </a>
                      )}
                      {expert.twitter_url && (
                        <a
                          href={expert.twitter_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <Twitter className="w-4 h-4" />
                        </a>
                      )}
                      {expert.website_url && (
                        <a
                          href={expert.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <Globe className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleOpenDialog(expert)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setDeletingExpert(expert);
                          setIsDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingExpert ? 'Edit Expert' : 'Add New Expert'}
            </DialogTitle>
            <DialogDescription>
              {editingExpert
                ? 'Update expert information'
                : 'Create a new expert profile for content interviews'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Input
                  id="role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  placeholder="CEO, CTO, Expert..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company</Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Company name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Brief biography..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expertise">Expertise Areas</Label>
              <Input
                id="expertise"
                value={formData.expertise}
                onChange={(e) => setFormData({ ...formData, expertise: e.target.value })}
                placeholder="e.g., Machine Learning, Data Science, AI (comma-separated)"
              />
              <p className="text-xs text-muted-foreground">
                Enter expertise areas separated by commas
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="photo_url">Photo URL</Label>
              <Input
                id="photo_url"
                value={formData.photo_url}
                onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                placeholder="https://example.com/photo.jpg"
              />
            </div>

            <div className="space-y-2">
              <Label>Social Links</Label>
              <div className="grid grid-cols-1 gap-3">
                <Input
                  value={formData.linkedin_url}
                  onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                  placeholder="LinkedIn URL"
                />
                <Input
                  value={formData.twitter_url}
                  onChange={(e) => setFormData({ ...formData, twitter_url: e.target.value })}
                  placeholder="Twitter URL"
                />
                <Input
                  value={formData.website_url}
                  onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                  placeholder="Website URL"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSaveExpert} disabled={saving}>
              {saving ? 'Saving...' : editingExpert ? 'Update Expert' : 'Create Expert'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Expert</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {deletingExpert?.name}? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setDeletingExpert(null);
              }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteExpert} disabled={saving}>
              {saving ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
